import React, { useState, useRef, useCallback, useEffect } from "react";
import { Box, IconButton } from "@mui/material";
import {
  CloseRounded,
  PrecisionManufacturingRounded,
  CameraAltRounded,
  DescriptionRounded,
  BuildRounded,
} from "@mui/icons-material";
import ChatBotMessage from "../../../shared/chatBot/components/ChatBotMessage";
import ChatUserMessage from "../../../shared/chatBot/components/ChatUserMessage";
import ChatTypingIndicator from "../../../shared/chatBot/components/ChatTypingIndicator";
import ChatStreamingMessage from "../../../shared/chatBot/components/ChatStreamingMessage";
import ChatDaySeparator from "../../../shared/chatBot/components/ChatDaySeparator";
import ChatInputArea from "../../../shared/chatBot/components/ChatInputArea";
import MidasLogo from "../../../customer/midas/components/MidasLogo";
import chatBotApi from "../../../shared/chatBot/ChatBotApi";
import {
  readSSEStream,
  mapBotSources,
  mapBotArtifacts,
  mapBotActions,
} from "../../../shared/chatBot/streamBotMessage";
import { ChatMessage } from "../../../shared/chatBot/models/chatBotModels";
import useTranslation from "../../../shared/hooks/useTranslation";
import { dt } from "../../../shared/themes/redesignTokens";

// ===================================================================
//  Types
// ===================================================================

export interface KioskMachineData {
  /** Machine GUID — validated before use */
  machineId: string;
  model: string;
  serialNumber: string;
  controllerSystem?: string;
  manufacturerName?: string;
}

interface KioskMidasChatProps {
  machine: KioskMachineData;
  onClose: () => void;
}

// ===================================================================
//  Constants
// ===================================================================

const MIDAS_GOLD = "#D4A017";
const MIDAS_GOLD_DARK = "#A67C00";
const MIDAS_GOLD_SOFT = "rgba(212, 160, 23, 0.08)";

/** UUID v4 regex for SEC-REQ-5 validation */
const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// ===================================================================
//  Helpers
// ===================================================================

const generateId = (): string => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
};

const isDifferentDay = (a: string, b: string): boolean => {
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() !== db.getFullYear() ||
    da.getMonth() !== db.getMonth() ||
    da.getDate() !== db.getDate()
  );
};

const isNewGroup = (a: ChatMessage, b: ChatMessage): boolean => {
  const diff = Math.abs(
    new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
  return diff > 5 * 60 * 1000;
};

// ===================================================================
//  KioskMidasChat — full-screen overlay for QR kiosk
//
//  AUTH NOTE: The QR kiosk page (/public/machines/:id) requires
//  authentication (PIN code or login). Both frontend AuthorizedRoute
//  and backend [Authorize] are in place. This is NOT a public page.
// ===================================================================

const KioskMidasChat: React.FC<KioskMidasChatProps> = ({
  machine,
  onClose,
}) => {
  const { translate } = useTranslation("public");

  // ── Self-contained local state (no chatBotSlice / midasPageSlice) ──
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState<string | null>(null);
  const [streamingText, setStreamingText] = useState<string | null>(null);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const listRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  // SEC-REQ-5: Validate machine_id is a valid GUID format
  const isValidMachineId = UUID_REGEX.test(machine.machineId);

  // ── Create thread on mount ──
  useEffect(() => {
    if (!isValidMachineId) return;

    let cancelled = false;
    const init = async () => {
      try {
        const thread = await chatBotApi.createThread(machine.machineId);
        if (!cancelled) {
          setThreadId(thread.id);
        }
      } catch {
        // Thread creation failed — chat will still work without thread persistence.
        // Do NOT set error here — a missing thread is non-fatal.
      }
    };
    init();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [machine.machineId, isValidMachineId]);

  // ── Lock body scroll while overlay is open ──
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // ── Auto-scroll on new messages or streaming text updates ──
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, isLoading, streamingText]);

  // ── Send message handler ──
  const handleSend = useCallback(
    async (text: string, image?: File) => {
      if (!isValidMachineId) return;

      // Build user message
      let imageUrl: string | undefined;
      let imageName: string | undefined;
      if (image) {
        imageUrl = URL.createObjectURL(image);
        imageName = image.name;
      }

      if (text || image) {
        const userMsg: ChatMessage = {
          id: generateId(),
          role: "user",
          text,
          timestamp: new Date().toISOString(),
          imageUrl,
          imageName,
        };
        setMessages((prev) => [...prev, userMsg]);
      }

      setIsLoading(true);
      setCurrentStep(null);
      setStreamingText(null);
      setError(null);

      // Try SSE streaming first
      let response: Response | null = null;
      try {
        response = await chatBotApi.streamMessage(
          text,
          machine.machineId,
          threadId || undefined,
          image
        );
      } catch {
        // Fall back to sync
      }

      if (response) {
        let streamError: string | null = null;
        let messageHandled = false;
        const botMessage = await readSSEStream(response, {
          onStep: (stepKey) => setCurrentStep(stepKey),
          onMessage: (msg) => {
            // Add bot message to state as soon as the answer event arrives
            // (before the stream fully closes). This ensures the user sees
            // the response even if the SSE connection stays open longer.
            if (!messageHandled) {
              messageHandled = true;
              setStreamingText(null);
              setMessages((prev) => [...prev, msg]);
              setIsLoading(false);
              setCurrentStep(null);
            }
          },
          onError: (errMsg) => {
            streamError = errMsg;
            setStreamingText(null);
            setError(errMsg);
          },
          onToken: (text) => {
            // Progressive text rendering — append token chunk to streaming state
            setStreamingText((prev) => (prev || "") + text);
          },
        });

        // If the message was already added via onMessage callback, skip.
        // Otherwise add via the return value (backward compat).
        if (!messageHandled && botMessage) {
          setMessages((prev) => [...prev, botMessage]);
        } else if (!messageHandled && !streamError) {
          // SSE stream completed without an answer event and without an error.
          // Fall back to synchronous endpoint.
          try {
            const syncRes = await chatBotApi.sendMessage(
              text,
              machine.machineId,
              threadId || undefined,
              image
            );
            const botMsg: ChatMessage = {
              id: generateId(),
              role: "assistant",
              text: syncRes.message,
              timestamp: new Date().toISOString(),
              sources: mapBotSources(syncRes.sources),
              suggestions: syncRes.suggestions,
              intent: syncRes.metadata?.intent,
              artifacts: mapBotArtifacts((syncRes as any).artifacts),
              actions: mapBotActions((syncRes as any).actions),
            };
            setMessages((prev) => [...prev, botMsg]);
          } catch {
            setError("Failed to get response from MIDAS");
          }
        }
      } else {
        // SSE streaming failed entirely — sync fallback
        try {
          const syncRes = await chatBotApi.sendMessage(
            text,
            machine.machineId,
            threadId || undefined,
            image
          );
          const botMsg: ChatMessage = {
            id: generateId(),
            role: "assistant",
            text: syncRes.message,
            timestamp: new Date().toISOString(),
            sources: mapBotSources(syncRes.sources),
            suggestions: syncRes.suggestions,
            intent: syncRes.metadata?.intent,
            artifacts: mapBotArtifacts((syncRes as any).artifacts),
            actions: mapBotActions((syncRes as any).actions),
          };
          setMessages((prev) => [...prev, botMsg]);
        } catch {
          setError("Failed to get response from MIDAS");
        }
      }

      setIsLoading(false);
      setCurrentStep(null);
      setStreamingText(null);
    },
    [machine.machineId, threadId, isValidMachineId]
  );

  // ── Suggestion chip click ──
  const handleSuggestionClick = useCallback(
    (text: string) => {
      handleSend(text);
    },
    [handleSend]
  );

  // ── Machine display label ──
  const machineLabel = [machine.model, machine.serialNumber ? `(S/N ${machine.serialNumber})` : ""]
    .filter(Boolean)
    .join(" ");

  // ── Suggestion chips for empty state ──
  const suggestions = [
    { icon: <CameraAltRounded sx={{ fontSize: 16 }} />, text: translate("kioskMidasSuggestionPhoto") },
    { icon: <DescriptionRounded sx={{ fontSize: 16 }} />, text: translate("kioskMidasSuggestionAlarms") },
    { icon: <BuildRounded sx={{ fontSize: 16 }} />, text: translate("kioskMidasSuggestionMaintenance") },
  ];

  const isEmpty = messages.length === 0 && !isLoading;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1300,
        display: "flex",
        flexDirection: "column",
        backgroundColor: dt.pageBg,
        animation: "kioskMidasSlideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1) both",
        "@keyframes kioskMidasSlideUp": {
          from: { transform: "translateY(100%)", opacity: 0 },
          to: { transform: "translateY(0)", opacity: 1 },
        },
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "12px 16px",
          backgroundColor: dt.cardBg,
          borderBottom: `1px solid ${dt.cardBorder}`,
          flexShrink: 0,
        }}
      >
        <MidasLogo size="sm" />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "15px",
              fontWeight: 700,
              color: dt.textPrimary,
            }}
          >
            {translate("kioskMidasTitle")}
          </Box>
          {/* Gold machine context chip */}
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              mt: "2px",
              padding: "2px 8px",
              borderRadius: "12px",
              backgroundColor: MIDAS_GOLD_SOFT,
              border: `1px solid rgba(212, 160, 23, 0.25)`,
            }}
          >
            <PrecisionManufacturingRounded
              sx={{ fontSize: 12, color: MIDAS_GOLD }}
            />
            <Box
              component="span"
              sx={{
                fontFamily: dt.fontMono,
                fontSize: "11px",
                fontWeight: 500,
                color: MIDAS_GOLD_DARK,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "200px",
              }}
            >
              {machineLabel}
            </Box>
          </Box>
        </Box>

        {/* Close button */}
        <IconButton
          onClick={onClose}
          aria-label={translate("kioskMidasClose")}
          sx={{
            width: 40,
            height: 40,
            color: dt.textSecondary,
            "&:hover": { color: dt.textPrimary },
          }}
        >
          <CloseRounded />
        </IconButton>
      </Box>

      {/* ── Message area ── */}
      <Box
        ref={listRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          padding: "16px",
          background: dt.pageBg,
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgba(52,152,219,0.04) 1px, transparent 0)",
          backgroundSize: "28px 28px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        {/* Empty state */}
        {isEmpty && (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "24px",
              padding: "32px 16px",
            }}
          >
            <MidasLogo size="md" />
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "16px",
                fontWeight: 600,
                color: dt.textPrimary,
                textAlign: "center",
              }}
            >
              {translate("kioskMidasEmptyState")}
            </Box>

            {/* Suggestion chips */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                width: "100%",
                maxWidth: "320px",
              }}
            >
              {suggestions.map((s, i) => (
                <Box
                  key={i}
                  onClick={() => handleSuggestionClick(s.text)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "12px 16px",
                    borderRadius: "12px",
                    border: `1.5px solid rgba(212, 160, 23, 0.3)`,
                    backgroundColor: "transparent",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    animation: `kioskChipFade 0.3s ease ${i * 80}ms both`,
                    "@keyframes kioskChipFade": {
                      from: { opacity: 0, transform: "translateY(8px)" },
                      to: { opacity: 1, transform: "translateY(0)" },
                    },
                    "&:hover": {
                      backgroundColor: MIDAS_GOLD_SOFT,
                      borderColor: MIDAS_GOLD,
                    },
                    "&:active": { transform: "scale(0.98)" },
                  }}
                >
                  <Box sx={{ color: MIDAS_GOLD, flexShrink: 0 }}>{s.icon}</Box>
                  <Box
                    sx={{
                      fontFamily: dt.font,
                      fontSize: "14px",
                      fontWeight: 500,
                      color: dt.textSecondary,
                    }}
                  >
                    {s.text}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Messages */}
        {messages.map((msg, index) => {
          const prev = index > 0 ? messages[index - 1] : null;
          const next =
            index < messages.length - 1 ? messages[index + 1] : null;

          const showDaySeparator =
            !prev || isDifferentDay(prev.timestamp, msg.timestamp);
          const isFirstInGroup =
            !prev || prev.role !== msg.role || isNewGroup(prev, msg);
          const isLastInGroup =
            !next || next.role !== msg.role || isNewGroup(msg, next);

          return (
            <React.Fragment key={msg.id}>
              {showDaySeparator && <ChatDaySeparator date={msg.timestamp} />}
              {msg.role === "assistant" ? (
                <ChatBotMessage
                  message={msg}
                  showAvatar={isFirstInGroup}
                  showTimestamp={isLastInGroup}
                  onSuggestionClick={handleSuggestionClick}
                  isPhone
                  isKiosk
                />
              ) : (
                <ChatUserMessage
                  message={msg}
                  showTimestamp={isLastInGroup}
                  isPhone
                />
              )}
            </React.Fragment>
          );
        })}

        {/* Streaming message — show progressive text when tokens are arriving */}
        {isLoading && streamingText && (
          <ChatStreamingMessage
            text={streamingText}
            showAvatar={
              messages.length === 0 ||
              messages[messages.length - 1]?.role !== "assistant"
            }
          />
        )}

        {/* Typing indicator — show only when loading but no tokens yet */}
        {isLoading && !streamingText && (
          <ChatTypingIndicator
            currentStep={currentStep}
            showAvatar={
              messages.length === 0 ||
              messages[messages.length - 1]?.role !== "assistant"
            }
          />
        )}

        {/* Error */}
        {error && !isLoading && (
          <Box
            sx={{
              ml: "36px",
              fontFamily: dt.font,
              fontSize: "13px",
              color: "#E74C3C",
              backgroundColor: "rgba(231, 76, 60, 0.06)",
              border: "1px solid rgba(231, 76, 60, 0.15)",
              borderRadius: "8px",
              padding: "10px 14px",
            }}
          >
            {error}
          </Box>
        )}

        <div ref={bottomRef} />
      </Box>

      {/* ── Input area ── */}
      <ChatInputArea onSend={handleSend} disabled={isLoading} />
    </Box>
  );
};

export default KioskMidasChat;
